import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import PricingCard from "@/components/marketing/PricingCard";
import api from "@/lib/api";

/**
 * /pricing/clubs — Club Subscription Plans page (iter57).
 *
 * Three-tier ladder: Essentials $249/mo → Professional $499/mo → Signature
 * Custom. Positions Professional as the recommended plan. Reinforces the
 * core narrative: "My Crest Club does not replace your golf shop; it
 * extends it." Includes the value bar, four business-outcome benefits,
 * compact comparison table, and final CTA per spec.
 */
export default function ClubPricingPage() {
  const [tiers, setTiers] = useState([]);

  useEffect(() => {
    api.get("/public/pricing/tiers")
      .then(({ data }) => setTiers(data.club_tiers || []))
      .catch(() => setTiers([]));
  }, []);

  return (
    <MarketingLayout>
      {/* Page header */}
      <section data-testid="club-pricing-header" className="bg-[#F9F8F4]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 py-24 sm:py-28 text-center">
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
            Club Subscription Plans
          </div>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A422D] leading-[1.05]">
            One platform. Three solutions. Every membership included.
          </h1>
          <p className="mt-8 text-lg text-[#4A4A4A] leading-relaxed max-w-3xl mx-auto">
            My Crest Club gives private clubs a better way to manage member merchandise requests, capture demand, recover lost sales, and extend the golf shop experience beyond the physical shop. Every subscription includes unlimited member accounts with no per-member fees.
          </p>
          <p className="mt-8 max-w-2xl mx-auto text-sm sm:text-base italic text-[#8A6A2A]">
            My Crest Club does not replace your golf shop. It gives your golf shop a better way to serve members when the merchandise they want is not already on the shelf.
          </p>
        </div>
      </section>

      {/* Pricing cards */}
      <section data-testid="club-pricing-cards" className="bg-[#F9F8F4]">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pb-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            {tiers.map((tier) => {
              const variant = tier.featured ? "featured" : "default";
              const ctaTo = tier.key === "signature"
                ? "/contact?plan=signature&role=club"
                : `/contact?plan=${tier.key}&role=club`;
              return (
                <PricingCard
                  key={tier.key}
                  tier={tier}
                  variant={variant}
                  ctaTo={ctaTo}
                  testId={`club-tier-${tier.key}`}
                />
              );
            })}
          </div>

          <div
            data-testid="club-pricing-progression"
            className="mt-16 max-w-3xl mx-auto flex flex-wrap items-center justify-center gap-3 text-[11px] uppercase tracking-[0.28em] font-semibold text-[#B59A5A]"
          >
            <span>Operate</span>
            <span aria-hidden className="text-[#D9C68A]">→</span>
            <span>Grow</span>
            <span aria-hidden className="text-[#D9C68A]">→</span>
            <span>Customize</span>
          </div>
        </div>
      </section>

      <ValueBar />
      <WhyClubsSection />
      <ComparisonTable />
      <FinalCta />
    </MarketingLayout>
  );
}

function ValueBar() {
  const cols = [
    { title: "Unlimited Members", body: "Every subscription includes unlimited member accounts. No per-member fees." },
    { title: "Dedicated to Your Success", body: "From onboarding through ongoing support, My Crest Club helps your team successfully launch and use the platform." },
    { title: "Built for Private Clubs", body: "Designed specifically around private-club merchandising, member service, and golf shop operations." },
  ];
  return (
    <section data-testid="club-pricing-value-bar" className="bg-[#1A422D] text-[#F9F8F4]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-20 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
        {cols.map((c, i) => (
          <div key={c.title} data-testid={`club-value-${i + 1}`}>
            <div className="text-[12px] uppercase tracking-[0.28em] font-semibold text-[#D9C68A]">
              {c.title}
            </div>
            <p className="mt-4 text-[#F9F8F4]/85 leading-relaxed">{c.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhyClubsSection() {
  const blocks = [
    { n: "01", title: "Capture Lost Demand", body: "Give members a way to request merchandise even when the right item, size, or color is not currently in the golf shop." },
    { n: "02", title: "Extend the Golf Shop", body: "Allow seasonal and traveling members to stay connected to their club's merchandise experience wherever they are." },
    { n: "03", title: "Understand What Members Want", body: "Turn member requests, sizes, brands, categories, and buying behavior into actionable merchandising intelligence." },
    { n: "04", title: "Keep the Club in Control", body: "Members request. Your golf shop reviews, manages, and fulfills. My Crest Club supports the club rather than competing with it." },
  ];
  return (
    <section data-testid="club-pricing-why" className="bg-[#F9F8F4]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-14 py-24 sm:py-28">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
            More Than Order Management
          </div>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A422D] leading-[1.1]">
            Turn member demand into better service and smarter buying decisions.
          </h2>
        </div>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {blocks.map((b) => (
            <div
              key={b.n}
              data-testid={`club-why-${b.n}`}
              className="p-7 lg:p-8 bg-white border border-[#E5E3D8] hover:border-[#B59A5A] hover:-translate-y-1 transition-all duration-300"
            >
              <div className="font-serif text-2xl text-[#D9C68A]">{b.n}</div>
              <h3 className="mt-4 font-serif text-xl text-[#1A422D] leading-tight">{b.title}</h3>
              <p className="mt-4 text-sm text-[#4A4A4A] leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Comparison table
// ============================================================================
const COMPARISON_ROWS = [
  { label: "Unlimited Members", essentials: true, professional: true, signature: true },
  { label: "Unlimited Vendors", essentials: true, professional: true, signature: true },
  { label: "Member Special Requests", essentials: true, professional: true, signature: true },
  { label: "Photo Upload Requests", essentials: true, professional: true, signature: true },
  { label: "Wish Lists", essentials: true, professional: true, signature: true },
  { label: "Order Tracking", essentials: true, professional: true, signature: true },
  { label: "Basic Reporting", essentials: true, professional: true, signature: true },
  { label: "Advanced Reporting", essentials: false, professional: true, signature: true },
  { label: "Lost Sales Dashboard", essentials: false, professional: true, signature: true },
  { label: "Brand Demand Analytics", essentials: false, professional: true, signature: true },
  { label: "Size Demand Analytics", essentials: false, professional: true, signature: true },
  { label: "Seasonal Buying Reports", essentials: false, professional: true, signature: true },
  { label: "Executive Dashboards", essentials: false, professional: false, signature: true },
  { label: "Predictive Buying Analytics", essentials: false, professional: false, signature: true },
  { label: "API Integrations", essentials: false, professional: false, signature: true },
  { label: "Custom Workflows", essentials: false, professional: false, signature: true },
  { label: "Dedicated Account Manager", essentials: false, professional: false, signature: true },
];

function ComparisonTable() {
  const Cell = ({ on }) => (
    <div className="flex justify-center">
      {on ? (
        <Check size={17} className="text-[#1A422D]" strokeWidth={2.2} />
      ) : (
        <Minus size={17} className="text-[#B59A5A]/50" strokeWidth={2} />
      )}
    </div>
  );
  return (
    <section data-testid="club-pricing-compare" className="bg-[#F1EFE6] border-y border-[#E5E3D8]">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-24 sm:py-28">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-[12px] uppercase tracking-[0.32em] font-semibold text-[#B59A5A]">
            Compare Plans
          </div>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A422D] leading-[1.1]">
            Compare Club Plans.
          </h2>
        </div>

        <div className="mt-14 overflow-x-auto">
          <table className="w-full border-collapse min-w-[560px]">
            <thead>
              <tr className="border-b border-[#D9C68A]/40">
                <th className="text-left py-5 px-4 text-[11px] uppercase tracking-[0.24em] font-semibold text-[#7A7A7A]">
                  Feature
                </th>
                <th className="text-center py-5 px-4 font-serif text-lg text-[#1A422D]">Essentials</th>
                <th className="text-center py-5 px-4 font-serif text-lg text-[#1A422D] bg-[#1A422D]/5">Professional</th>
                <th className="text-center py-5 px-4 font-serif text-lg text-[#1A422D]">Signature</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-[#E5E3D8] hover:bg-white/40 transition-colors">
                  <td className="py-4 px-4 text-sm text-[#4A4A4A]">{row.label}</td>
                  <td className="py-4 px-4"><Cell on={row.essentials} /></td>
                  <td className="py-4 px-4 bg-[#1A422D]/5"><Cell on={row.professional} /></td>
                  <td className="py-4 px-4"><Cell on={row.signature} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section data-testid="club-pricing-final-cta" className="bg-[#F9F8F4]">
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-24 sm:py-32 text-center">
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A422D] leading-[1.1]">
          Give your members a better way to shop their club.
        </h2>
        <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">
          Bring member requests, merchandise demand, and special orders into one club-controlled platform built specifically for the private-club experience.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link
            to="/contact?role=club"
            data-testid="club-pricing-cta-apply"
            className="inline-flex items-center gap-2 px-9 py-4 bg-[#1A422D] text-[#F9F8F4] text-[13px] uppercase tracking-[0.24em] font-semibold hover:bg-[#0F2A1C] transition-colors"
          >
            Start Your Club Application <span aria-hidden>→</span>
          </Link>
          <Link
            to="/contact?role=club&reason=demo"
            data-testid="club-pricing-cta-schedule"
            className="inline-flex items-center gap-2 px-9 py-4 border border-[#1A422D] text-[#1A422D] text-[13px] uppercase tracking-[0.24em] font-semibold hover:bg-[#1A422D] hover:text-[#F9F8F4] transition-colors"
          >
            Schedule a Conversation
          </Link>
        </div>
        <p className="mt-8 text-sm text-[#7A7A7A]">
          Questions? Contact{" "}
          <a href="mailto:theshop@mycrestclub.com" className="text-[#1A422D] font-semibold underline underline-offset-4 hover:text-[#B59A5A]">
            theshop@mycrestclub.com
          </a>
        </p>
      </div>
    </section>
  );
}
